'use strict';

import React, {ChangeEvent} from 'react';
import moment from 'moment-timezone';

import metaModel from '~/shared/src/utilities/metaModel';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {FormFieldProperties} from '~/shared/src/utilities/modules/form';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';

import ColorField from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';
import CoordinateField from '~/frontend/src/components/base/shared/field/valueTypeFields/CoordinateField';
import CurrencyField from '~/frontend/src/components/base/shared/field/valueTypeFields/CurrencyField';
import DateField from '~/frontend/src/components/base/shared/field/valueTypeFields/DateField';
import DoubleField from '~/frontend/src/components/base/shared/field/valueTypeFields/DoubleField';
import DurationField from '~/frontend/src/components/base/shared/field/valueTypeFields/DurationField';
import EmailField from '~/frontend/src/components/base/shared/field/valueTypeFields/EmailField';
import IbanField from '~/frontend/src/components/base/shared/field/valueTypeFields/IbanField';
import ImageDataField from '~/frontend/src/components/base/shared/field/valueTypeFields/ImageDataField';
import IntegerField from '~/frontend/src/components/base/shared/field/valueTypeFields/IntegerField';
import JsonField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField';
import LinkField from '~/frontend/src/components/base/shared/field/valueTypeFields/LinkField';
import PhoneField from '~/frontend/src/components/base/shared/field/valueTypeFields/PhoneField';
import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import TextField from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import TimeField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimeField';
import TimestampField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimestampField';
import ValueSetField from '~/frontend/src/components/base/shared/field/ValueSetField';


export type Params = {
    BOOLEAN : {};
    COLOR : {};
    COORDINATE : {};
    CURRENCY : {
        valueUnit : string;
    };
    DATE : {};
    DOUBLE : {
        valueUnit : string;
        valuePrecision? : number;
    };
    DURATION : {};
    EMAIL : {};
    IBAN : {};
    IMAGEDATA : {
        height : number;
    };
    INTEGER : {
        valueUnit : string;
    };
    JSON : {
        height : number;
    };
    LINK : {};
    PHONE : {};
    STATUS : {
        returnMode : 'asInput' | 'id' | 'name';
        schema : Schema;
        showAdditionalRawNames : boolean;
    },
    STATUSID : {
        returnMode : 'asInput' | 'id' | 'name';
        showAdditionalRawNames : boolean;
    },
    STATUSNAME : {
        returnMode : 'asInput' | 'id' | 'name';
        showAdditionalRawNames : boolean;
    },
    STRING : {};
    TEXT : {
        height : number;
    };
    TIME : {};
    TIMERULE : {
        timezone : string;
        language : string;
    };
    TIMESTAMP : {
        timezone : string;
    };
}

// TODO: mapped type for params depending on type
// TODO: mapped type for editorParams depending on editor

/**
 * Returns an editor for the given type
 *
 * @param {FilterValueType} type
 * @param {any} value
 * @param {(value : any, originalEvent : any) => void} onChange
 * @param {boolean} rawMode
 * @param {Object} params
 * @param {Object} editorParams
 */
export const getValueEditor = ( type : FilterValueType, value : any, onChange : ( value : any, originalEvent : any ) => void, rawMode : boolean = false, params : any = {}, editorParams : any = {} ) : JSX.Element | undefined => {
    let retVal : JSX.Element | undefined;

    switch ( type ) {
        case 'BOOLEAN':
            //retVal = getBoolEditor();
            break;

        case 'COLOR':
            retVal = getColorEditor( value, onChange, rawMode, editorParams );
            break;

        case 'COORDINATE':
            retVal = getCoordinateEditor( value, onChange, rawMode, editorParams );
            break;

        case 'CURRENCY':
            retVal = getCurrencyEditor( value, onChange, params.valueUnit, rawMode, editorParams );
            break;

        case 'DATE':
            retVal = getDateEditor( value, onChange, rawMode, editorParams );
            break;

        case 'DOUBLE':
            retVal = getDoubleEditor( value, onChange, params.valueUnit, params.valuePrecision, rawMode, editorParams );
            break;

        case 'DURATION':
            retVal = getDurationEditor( value, onChange, rawMode, editorParams );
            break;

        case 'EMAIL':
            retVal = getEmailEditor( value, onChange, editorParams );
            break;

        case 'IBAN':
            retVal = getIbanEditor( value, onChange, rawMode, editorParams );
            break;

        case 'IMAGEDATA':
            retVal = getImageDataEditor( value, onChange, params.height, rawMode, editorParams );
            break;

        case 'INTEGER':
            retVal = getIntegerEditor( value, onChange, params.valueUnit, rawMode, editorParams );
            break;

        case 'JSON':
            retVal = getJsonEditor( value, onChange, params.height, rawMode, editorParams );
            break;

        case 'LINK':
            retVal = getLinkEditor( value, onChange, rawMode, editorParams );
            break;

        case 'PHONE':
            retVal = getPhoneEditor( value, onChange, rawMode, editorParams );
            break;

        case 'STATUS':
        case 'STATUSID':
        case 'STATUSNAME':
            retVal = getStatusEditor( value, onChange, params.schema, rawMode, params.returnMode, editorParams )
            break;

        case 'STRING':
            retVal = getStringEditor( value, onChange, editorParams );
            break;

        case 'TEXT':
            retVal = getTextEditor( value, onChange, params.height, rawMode, editorParams );
            break;

        case 'TIME':
            retVal = getTimeEditor( value, onChange, rawMode, editorParams );
            break;

        case 'TIMERULE':
            retVal = getTimeRuleEditor( value, onChange, params.language, params.timezone, rawMode, editorParams );
            break;

        case 'TIMESTAMP':
            retVal = getTimestampEditor( value, onChange, params.timezone, rawMode, editorParams );
            break;

    }

    return retVal;
}

/**
 * Returns an editor for the given valueSet
 *
 * @param {Schema} schema
 * @param {'property' | 'referenceProperty'} type
 * @param {number} setId
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {boolean} [showAdditionalRawNames=false]
 * @param {any} [editorParams={}]
 */
export const getValueSetEditor = ( schema : Schema, type : 'property' | 'referenceProperty' | 'static', setId : number, value : AttributeValue | undefined, onChange : ( value : AttributeValue | undefined, originalEvent : any ) => void, rawMode : boolean = false, showAdditionalRawNames : boolean = false, editorParams : any = {} ) : JSX.Element | undefined => {
    return <ValueSetField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'ValueSetField' ) as RequiredOf<FormFieldProperties>}

        valueSetId={type === 'static' ? setId : undefined}
        propertySetId={type === 'property' ? setId : undefined}
        referencePropertySetId={type === 'referenceProperty' ? setId : undefined}

        value={value}
        onChange={onChange}

        schema={schema}
        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a boolean value
 *
 * @param {boolean} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {any} [editorParams={}]
 */
export const getBoolEditor = ( value : boolean, onChange : ( value : boolean | undefined, originalEvent : any ) => void, editorParams : any = {} ) => {

    // TODO: implement if really needed

}

/**
 * Returns the editor for a string value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getColorEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <ColorField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'ColorField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a coordinate value
 *
 * @param {number[]} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getCoordinateEditor = ( value : number[], onChange : ( value : number[], originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <CoordinateField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'CoordinateField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a currency value
 *
 * @param {number} value
 * @param {(value : number, originalEvent : any) => void} onChange
 * @param {string} [valueUnit='']
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getCurrencyEditor = ( value : number, onChange : ( value : number | undefined, originalEvent : any ) => void, valueUnit : string = '', rawMode : boolean = false, editorParams : any = {} ) => {
    return <CurrencyField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'CurrencyField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        valueUnit={valueUnit}
        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a date value
 *
 * NOTE: The type of the value of the onChange callback depends on the type of the given value.
 *
 * @param {string|Date} value
 * @param {(value : string|Date, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getDateEditor = ( value : string | Date, onChange : ( value : string | Date | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    let inputValue : Date | undefined;

    if ( value !== undefined && value !== null && value !== '' ) {
        inputValue = moment( value, 'YYYY-MM-DD' ).toDate();
    }

    return <DateField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'DateField' ) as RequiredOf<FormFieldProperties>}

        value={inputValue}
        onChange={( dateValue : Date | undefined, e : any ) => {
            let outputValue : string | Date | undefined = dateValue;

            // input was a string -> convert output to string
            if ( typeof value === 'string' && outputValue ) {
                outputValue = moment( outputValue ).format( 'YYYY-MM-DD' );
            }

            onChange( outputValue, e );
        }}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a double value
 *
 * @param {number} value
 * @param {(value : number, originalEvent : any) => void} onChange
 * @param {string} [valueUnit='']
 * @param {number} [valuePrecision]
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getDoubleEditor = ( value : number, onChange : ( value : number | undefined, originalEvent : any ) => void, valueUnit : string = '', valuePrecision? : number, rawMode : boolean = false, editorParams : any = {} ) => {
    return <DoubleField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'DoubleField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        valueUnit={valueUnit}
        precision={valuePrecision}
        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a duration value
 *
 * @param {number} value
 * @param {(value : number, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getDurationEditor = ( value : number, onChange : ( value : number | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <DurationField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'DurationField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a email value
 *
 * @param {boolean} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getEmailEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, editorParams : any = {} ) => {
    return <EmailField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'EmailField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a IBAN value
 *
 * @param {string} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getIbanEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <IbanField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'IbanField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a imagedata value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {number} [height=90]
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getImageDataEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, height : number | false = 90, rawMode : boolean = false, editorParams : any = {} ) => {
    if ( !editorParams.inputStyle ) {
        editorParams.inputStyle = {};
    }
    if ( !editorParams.inputStyle.height && height !== false ) {
        editorParams.inputStyle.height = ( height - 5 ) + 'px';
    }
    delete editorParams.onResize;

    return <ImageDataField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'ImageDataField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={( imageData : string, e : ChangeEvent<HTMLInputElement> ) => {
            onChange( imageData, e );
        }}
        rawMode={rawMode}
        rows={3}
        {...editorParams}
    />;
}

/**
 * Returns the editor for a integer value
 *
 * @param {number} value
 * @param {(value : number, originalEvent : any) => void} onChange
 * @param {string} [valueUnit='']
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getIntegerEditor = ( value : number, onChange : ( value : number | undefined, originalEvent : any ) => void, valueUnit : string = '', rawMode : boolean = false, editorParams : any = {} ) => {
    return <IntegerField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'IntegerField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        valueUnit={valueUnit}
        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a json value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {number} [height=90]
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getJsonEditor = ( value : string, onChange : ( value : string, originalEvent : any ) => void, height : number | false = 90, rawMode : boolean = false, editorParams : any = {} ) => {
    if ( value !== undefined && value !== null ) {
        try {
            value = JSON.stringify( JSON.parse( value ), null, '    ' );
        }
        catch ( e ) {
        }
    }

    if ( !editorParams.inputStyle ) {
        editorParams.inputStyle = {};
    }
    if ( !editorParams.inputStyle.height && height !== false ) {
        editorParams.inputStyle.height = height + 'px';
    }

    return <JsonField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'TextField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        {...editorParams}

        editMode="plaintext"
    />;
}

/**
 * Returns the editor for a link value
 *
 * @param {boolean} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getLinkEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <LinkField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'LinkField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a phone value
 *
 * @param {boolean} value
 * @param {(value : boolean, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getPhoneEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    return <PhoneField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'PhoneField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for status
 *
 * @param {number | string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {Schema} schema
 * @param {boolean} [rawMode=false]
 * @param {'asInput' | 'id' | 'name'} [returnMode='asInput']
 * @param {any} [editorParams={}]
 */
export const getStatusEditor = ( value : number | string, onChange : ( value : number | string, originalEvent : any ) => void, schema : Schema, rawMode : boolean = false, returnMode : 'asInput' | 'id' | 'name' = 'asInput', editorParams : any = {} ) => {
    if ( returnMode === 'asInput' ) {
        if ( typeof value === 'number' ) {
            returnMode = 'id';
        }
        else {
            returnMode = 'name';
        }
    }

    if ( typeof value === 'number' ) {
        value = metaModel[ schema ].getStatus( value )?.name ?? value;
    }

    delete editorParams.onResize;

    return <StatusDropdown
        value={value}
        onChange={( status : string, e ) => {
            let statusValue : number | string = status;

            if ( returnMode === 'id' ) {
                statusValue = metaModel[ schema ].getStatus( status )?.id ?? status;
            }

            onChange( statusValue, e );
        }}
        schema={schema}

        mode="settable"
        markDirty={false}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a string value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {any} [editorParams={}]
 */
export const getStringEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, editorParams : any = {} ) => {
    return <StringField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'StringField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a text value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {number} [height=90]
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getTextEditor = ( value : string, onChange : ( value : string, originalEvent : any ) => void, height : number | false = 90, rawMode : boolean = false, editorParams : any = {} ) => {
    if ( !editorParams.inputStyle ) {
        editorParams.inputStyle = {};
    }
    if ( !editorParams.inputStyle.height && height !== false ) {
        editorParams.inputStyle.height = height + 'px';
    }

    return <TextField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'TextField' ) as RequiredOf<FormFieldProperties>}

        value={value}
        onChange={onChange}

        editMode={rawMode ? 'plaintext' : undefined}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a time value
 *
 * NOTE: The type of the value of the onChange callback depends on the type of the given value.
 *
 * @param {string|Date} value
 * @param {(value : string|Date, originalEvent : any) => void} onChange
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getTimeEditor = ( value : string | Date, onChange : ( value : string | Date | undefined, originalEvent : any ) => void, rawMode : boolean = false, editorParams : any = {} ) => {
    let inputValue : Date | undefined;

    if ( value !== undefined && value !== null && value !== '' ) {
        inputValue = moment( value, 'HH:mm:ss' ).toDate();
    }

    return <TimeField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'TimeField' ) as RequiredOf<FormFieldProperties>}

        value={inputValue}
        onChange={( dateValue : Date | undefined, e : any ) => {
            let outputValue : string | Date | undefined = dateValue;

            // input was a string -> convert output to string
            if ( typeof value === 'string' && outputValue ) {
                outputValue = moment( outputValue ).format( 'HH:mm:ss' );
            }

            onChange( outputValue, e );
        }}

        rawMode={rawMode}

        {...editorParams}
    />;
}

/**
 * Returns the editor for a timerule value
 *
 * @param {string} value
 * @param {(value : string, originalEvent : any) => void} onChange
 * @param {string} language
 * @param {string} [timezone='Europe/Berlin']
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getTimeRuleEditor = ( value : string, onChange : ( value : string | undefined, originalEvent : any ) => void, language : string, timezone : string = 'Europe/Berlin', rawMode : boolean = false, editorParams : any = {} ) => {

    // TODO: implement a real TimeRule editor for non rawMode

    return <TextField
        value={value}
        onChange={onChange}

        {...editorParams}

        editMode="plaintext"
    />;
}

/**
 * Returns the editor for a time value
 *
 * NOTE: The type of the value of the onChange callback depends on the type of the given value.
 *
 * @param {string|Date} value
 * @param {(value : string|Date, originalEvent : any) => void} onChange
 * @param {string} [timezone='Europe/Berlin']
 * @param {boolean} [rawMode=false]
 * @param {any} [editorParams={}]
 */
export const getTimestampEditor = ( value : string | Date, onChange : ( value : string | Date | undefined, originalEvent : any ) => void, timezone : string = 'Europe/Berlin', rawMode : boolean = false, editorParams : any = {} ) => {
    let inputValue : Date | undefined;

    if ( value !== undefined && value !== null && value !== '' ) {
        inputValue = moment( value, 'YYYY-MM-DD HH:mm:ss.sss' ).toDate();
    }

    return <TimestampField
        {...dataExtraction.getDefaultsForItemType( 'CFG', 'TimestampField' ) as RequiredOf<FormFieldProperties>}

        value={inputValue}
        onChange={( dateValue : Date | undefined, e : any ) => {
            let outputValue : string | Date | undefined = dateValue;

            // input was a string -> convert output to string
            if ( typeof value === 'string' && outputValue ) {
                outputValue = moment( outputValue ).format( 'YYYY-MM-DD HH:mm:ss.sss' );
            }

            onChange( outputValue, e );
        }}

        timezone={timezone}
        rawMode={rawMode}

        {...editorParams}
    />;
}
